import { Skeleton } from '@mui/material';
import Button from '@mui/material/Button';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Toggle from '/@/components/forms/toggle';
import ToggleGroup from '/@/components/forms/toggle-group';
import MapComponent from '/@/components/map';
import Title from '/@/components/title';
import settings from '/@/core/settings';
import { useFieldTrials, useFieldTrialsTypes } from '/@/hooks/field-trial-network';
import { useMyFields } from '/@/hooks/fields';
import { _ } from '/@/locale';
import { getSessionCountry } from '/@/store/session/selectors';
import { isMobile } from '/@/utils';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { PHENOLOGY_OPTIONS } from './phenology-options';
import styles from './styles.module.css';
import RenderMarkerDescription from './trial-description';

const ZOOM_VALUES = { initial: 4, zoomed: 16 } as const;

const phenologiesOptions = PHENOLOGY_OPTIONS;

const FieldTrialNetworkSidebar = memo(function FieldTrialNetworkSidebar({
    visible,
    onClose,
    trialTypesOptions,
    trialTypeToggleOptions,
    selectedTypes,
    onTrialTypeChange,
    selectedPhenologies,
    onPhenologyChange,
    campaignOptions,
    selectedCampaign,
    onCampaignChange,
    includeFields,
    onIncludeFieldsChange,
    isMobileView,
}: {
    visible: boolean;
    onClose: () => void;
    trialTypesOptions: { id: string | number; name: string }[] | undefined;
    trialTypeToggleOptions: { value: string | number; label: string }[];
    selectedTypes: (string | number)[];
    onTrialTypeChange: (types: (string | number)[]) => void;
    selectedPhenologies: string[];
    onPhenologyChange: (phenologies: string[]) => void;
    campaignOptions: { value: string; label: string }[];
    selectedCampaign: string[];
    onCampaignChange: (campaign: string[]) => void;
    includeFields: boolean;
    onIncludeFieldsChange: (value: boolean) => void;
    isMobileView: boolean;
}) {
    const size = 'lg';
    if (!visible) return null;

    return (
        <div className={styles.sidebar}>
            <div className={styles.closeSidebar} onClick={onClose}>
                <i className='material-icons'>close</i>
            </div>
            <Title size={size}>{_('Red de ensayos')}</Title>
            <div className={styles.text}>
                <p>
                    {_(
                        'A continuación podrás acceder a las ubicaciones de todos los ensayos de nuestra red. Utilizá los siguientes filtros para especificar tu búsqueda.'
                    )}
                </p>
            </div>
            <div className={styles.filter}>
                <Title size={size}>{_('Filtrá por tipo de ensayo')}</Title>
                {trialTypesOptions ? (
                    <ToggleGroup
                        type='checkbox'
                        theme={settings.app.theme}
                        max={trialTypesOptions.length}
                        options={trialTypeToggleOptions}
                        value={selectedTypes as never}
                        onChange={onTrialTypeChange}
                        direction
                    />
                ) : (
                    <Skeleton variant='rectangular' width={'100%'} height={60} />
                )}
            </div>
            <div className={styles.filter}>
                <Title size={size}>{_('Filtrá por estadio fenológico')}</Title>
                <ToggleGroup
                    theme={settings.app.theme}
                    type='checkbox'
                    max={phenologiesOptions.length}
                    options={phenologiesOptions}
                    value={selectedPhenologies as never}
                    onChange={onPhenologyChange}
                    direction
                />
            </div>
            <div className={styles.filter}>
                <Title size={size}>{_('Filtrá por año')}</Title>
                <ToggleGroup
                    theme={settings.app.theme}
                    type='checkbox'
                    max={campaignOptions.length}
                    options={campaignOptions}
                    value={selectedCampaign as never}
                    onChange={onCampaignChange}
                    direction
                />
            </div>
            <div className={styles.filter}>
                <Title size={size}>{_('Visualizá tus lotes en el mapa')}</Title>
                <Toggle
                    type='checkbox'
                    theme={settings.app.theme}
                    label={includeFields ? _('Ocultar') : _('Mostrar')}
                    value={includeFields}
                    onChange={onIncludeFieldsChange}
                />
            </div>
            {isMobileView && (
                <Button variant='outlined' fullWidth onClick={onClose}>
                    {_('Ver ensayos')}
                </Button>
            )}
        </div>
    );
});

const FieldTrialNetwork = () => {
    const isMobileView = useIsMobile();
    const country = useSelector(getSessionCountry);
    const fieldsQuery = useMyFields({
        page: 1,
        pageSize: 10000,
        country: country.code,
        selectOnly: 'id,lat,lng,geometry,name,area',
    });
    const parsedFields = useMemo(
        () =>
            (fieldsQuery.data?.results || []).map((field) => ({
                ...field,
                _id: field.id,
                markerType: 'field',
                name: field.name,
                area: field.area,
            })),
        [fieldsQuery.data]
    );

    const fieldTrialsQuery = useFieldTrials({ page: 1, pageSize: 10000 });

    const { data: trialTypesOptions } = useFieldTrialsTypes({ page: 1, pageSize: 100 });

    const trialTypeToggleOptions = useMemo(
        () => (trialTypesOptions ?? []).map((t) => ({ value: t.id, label: _(t.name) })),
        [trialTypesOptions]
    );

    const campaignOptions = useMemo(() => {
        const options = (fieldTrialsQuery.data?.results || []).map((ft) => ({
            value: ft.campaign,
            label: ft.campaign,
        }));
        const map = new Map(options.map((option) => [option.label, option]));
        const uniquesOptions = [...map.values()] as { value: string; label: string }[];
        uniquesOptions.sort((a, b) => {
            return Number(String(a.value).substring(0, 4)) - Number(String(b.value).substring(0, 4));
        });
        return uniquesOptions;
    }, [fieldTrialsQuery.data?.results]);

    const [selectedTypes, setSelectedTypes] = useState<(string | number)[]>([]);
    const [selectedCampaign, setSelectedCampaign] = useState<string[]>([]);
    const [selectedPhenologies, setSelectedPhenologies] = useState<string[]>([]);
    const [sidebarIsVisible, setSidebarIsVisible] = useState(() => !isMobile());
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [includeFields, setIncludeFields] = useState(true);

    const offline = useSelector((state: { network: { offline: boolean } }) => state.network.offline);

    const filteredTrials = useMemo(() => {
        const results = fieldTrialsQuery?.data?.results || [];
        const typeSet = selectedTypes.length ? new Set(selectedTypes) : null;
        const phenologySet = selectedPhenologies.length ? new Set(selectedPhenologies) : null;
        const campaignSet = selectedCampaign.length ? new Set(selectedCampaign) : null;

        return results
            .filter((trial) => {
                if (typeSet && !typeSet.has(trial.type.id)) return false;
                if (phenologySet && !phenologySet.has(trial.phenologicalStage)) return false;
                if (campaignSet && !campaignSet.has(trial.campaign)) return false;
                return true;
            })
            .map((trial) => ({
                ...trial,
                lat: trial.latitude,
                lng: trial.longitude,
            }));
    }, [fieldTrialsQuery.data?.results, selectedTypes, selectedPhenologies, selectedCampaign]);

    const map = useRef<{
        focusMap: () => void;
        zoomMarker: (coords: { lat: number; lng: number }, zoom: number) => void;
        infoWindowRef: { current: google.maps.InfoWindow | null };
    } | null>(null);
    /** Avoid calling focusMap on every ref re-attach after state updates (it fits all bounds and resets zoom). */

    useEffect(() => {
        if (!trialTypesOptions?.length) return;
        const types = trialTypesOptions.map((type) => type.id);
        setSelectedTypes(types);
        setSelectedPhenologies(PHENOLOGY_OPTIONS.map((phenology) => phenology.value));
    }, [trialTypesOptions]);

    useEffect(() => {
        if (!campaignOptions.length) return;
        const campaigns = campaignOptions.map((campaign) => campaign.value);
        setSelectedCampaign(campaigns);
    }, [campaignOptions]);

    const closeDescription = useCallback(() => {
        if (!map.current) return;
        map.current.focusMap();
        setSelectedMarker(null);
        map.current.infoWindowRef.current?.close();
    }, []);

    const setMapRef = useCallback((ref) => {
        map.current = ref;
        if (!ref) return;
    }, []);

    const handleTrialTypeChange = useCallback((types) => {
        setSelectedTypes(types);
        setSelectedMarker(null);
    }, []);

    const handleTrialPhenologyChange = useCallback((phenologies) => {
        setSelectedPhenologies(phenologies);
        setSelectedMarker(null);
    }, []);

    const handleMarkerClick = useCallback((marker) => {
        if (!map.current) return;
        const coordinates = {
            lat: marker.latitude || marker.lat,
            lng: marker.longitude || marker.lng,
        };
        map.current.zoomMarker(coordinates, ZOOM_VALUES.zoomed);
        setSelectedMarker(marker);
    }, []);

    const hideSidebar = useCallback(() => setSidebarIsVisible(false), []);

    const handleSidebarToggle = useCallback(() => {
        setSidebarIsVisible(true);
        setSelectedMarker(null);
    }, []);

    const handleCampaignChange = useCallback((campaign) => {
        setSelectedCampaign(campaign);
        setSelectedMarker(null);
    }, []);

    const displayFilterButton = isMobileView && !sidebarIsVisible && !selectedMarker;

    const fields = useMemo(() => {
        return includeFields ? filteredTrials.concat(parsedFields) : filteredTrials;
    }, [includeFields, filteredTrials, parsedFields]);
    useEffect(() => {
        if (!map.current && fields.length > 0) return;
        map.current.focusMap();
    }, [fields]);

    return (
        <div className={styles.root}>
            <FieldTrialNetworkSidebar
                visible={sidebarIsVisible}
                onClose={hideSidebar}
                trialTypesOptions={trialTypesOptions}
                trialTypeToggleOptions={trialTypeToggleOptions}
                selectedTypes={selectedTypes}
                onTrialTypeChange={handleTrialTypeChange}
                selectedPhenologies={selectedPhenologies}
                onPhenologyChange={handleTrialPhenologyChange}
                campaignOptions={campaignOptions}
                selectedCampaign={selectedCampaign}
                onCampaignChange={handleCampaignChange}
                includeFields={includeFields}
                onIncludeFieldsChange={setIncludeFields}
                isMobileView={isMobileView}
            />
            <div className={styles.container}>
                <div className={styles.content}>
                    <MapComponent
                        ref={setMapRef}
                        fields={fields}
                        onFieldClick={handleMarkerClick}
                        offline={offline}
                        simplifyPolygons
                        isLoading={fieldTrialsQuery.isValidating || fieldsQuery.isValidating}
                    />
                </div>
                {displayFilterButton && (
                    <div className={styles.filterButton}>
                        <Button variant='outlined' fullWidth onClick={handleSidebarToggle}>
                            {_('Filtrar')}
                        </Button>
                    </div>
                )}
                {!!selectedMarker && (
                    <div className={styles.description}>
                        <RenderMarkerDescription
                            marker={selectedMarker}
                            onClose={closeDescription}
                            phenologiesOptions={phenologiesOptions}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default memo(FieldTrialNetwork);
